// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-ESIGN-1 — TanStack Query hooks for in-app signing (E-02: components never call api.* directly).

import { useEffect } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as api from '../../api/inappEsign'
import { mfaStatus } from '../../api/client'

export const inAppEsignKeys = {
  mode: ['carerx', 'signing-mode'] as const,
  mfa: ['auth', 'mfa-status'] as const,
  envelope: (id: string) => ['carerx', 'inapp-esign', 'envelope', id] as const,
  document: (id: string) => ['carerx', 'inapp-esign', 'document', id] as const,
  pending: ['carerx', 'inapp-esign', 'my-pending'] as const,
  pendingPage: (p: api.PendingParams) => ['carerx', 'inapp-esign', 'my-pending', p] as const,
  potPlan: (visitId: string) => ['carerx', 'pot', 'signing-plan', visitId] as const,
}

const TODAY: api.CareRxSigning = { mode: 'docusign', docusignReady: true }

/** The pharmacy's signing mode + whether DocuSign is set up (D-CRX-87 §4d). Until it loads: today's behaviour —
 *  'docusign', DocuSign assumed set up (the server still answers every send; the screen only explains). */
export function useCareRxSigning(): api.CareRxSigning {
  const q = useQuery({ queryKey: inAppEsignKeys.mode, queryFn: api.getCareRxSigning, staleTime: 60_000 })
  return q.data ?? TODAY
}

/** Which re-auth the signed-in user gives at signing: the authenticator code when MFA is on, else the password. */
export function useReauthKind(): 'totp' | 'password' | null {
  const q = useQuery({ queryKey: inAppEsignKeys.mfa, queryFn: mfaStatus, staleTime: 60_000 })
  return q.data ? (q.data.enabled ? 'totp' : 'password') : null
}

export function useInAppEnvelope(envelopeId: string | null) {
  return useQuery({
    queryKey: inAppEsignKeys.envelope(envelopeId ?? ''),
    queryFn: () => api.getInAppEnvelope(envelopeId!),
    enabled: !!envelopeId,
  })
}

/** The document bytes (object URL + SHA-256). The URL is revoked when the viewer goes away. */
export function useInAppDocument(envelopeId: string | null) {
  const q = useQuery({
    queryKey: inAppEsignKeys.document(envelopeId ?? ''),
    queryFn: () => api.fetchInAppDocument(envelopeId!),
    enabled: !!envelopeId,
    staleTime: Infinity,
    gcTime: 0,
  })
  const url = q.data?.url
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])
  return q
}

/** One server page of the signatures waiting on the caller; the previous page stays on screen while the next loads. */
export function useMyPendingSignatures(p: api.PendingParams, enabled = true) {
  return useQuery({ queryKey: inAppEsignKeys.pendingPage(p), queryFn: () => api.listMyPendingSignatures(p), enabled, placeholderData: keepPreviousData, staleTime: 15_000 })
}

/** D-POT-8: how this POT can go to its physician (in OwlRx and/or DocuSign) — fetched when the send dialog opens. */
export function usePotSigningPlan(visitId: string, enabled = true) {
  return useQuery({ queryKey: inAppEsignKeys.potPlan(visitId), queryFn: () => api.getPotSigningPlan(visitId), enabled, staleTime: 0 })
}

/** D-POT-8: send the POT to the physician's OwlRx portal; every POT list refetches (the badge turns Awaiting). */
export function useCreateInAppPot() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (documentId: string) => api.createInAppPot(documentId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['carerx', 'pot'] }); qc.invalidateQueries({ queryKey: inAppEsignKeys.pending }) },
  })
}

/** Sign; on success the envelope, the caller's pending list and the CareRx document lanes refetch — and the document:
 *  the server now serves the copy signed so far, so the signer sees their signature on its line (D-ESIGN-1 O3). */
export function useSignInApp(envelopeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { signerId: string; body: api.SignBody }) => api.signInApp(envelopeId, v.signerId, v.body),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: inAppEsignKeys.envelope(envelopeId) })
      qc.invalidateQueries({ queryKey: inAppEsignKeys.document(envelopeId) })
      qc.invalidateQueries({ queryKey: inAppEsignKeys.pending })
    },
  })
}

export function useCreateInAppEnvelope() {
  const qc = useQueryClient()
  return useMutation({
    // The enrollment form only (pharmacy staff sign in OwlRx); every other document stays DocuSign.
    mutationFn: (v: { kind: 'enrollment'; therapyId: string; pharmacistAccountId: string; providerFirst: boolean }) =>
      api.createInAppEnrollment(v.therapyId, { pharmacist_account_id: v.pharmacistAccountId, provider_first: v.providerFirst }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: inAppEsignKeys.pending }) },
  })
}
